<template>
  <header :class="['header', {
    'header--scrolled': isScrolled,
    'is-active': isActive,
    'search-active': isSearchActive
  }]">
    <div class="container">
      <!-- 左侧 Logo -->
      <div class="header__left">
        <router-link to="/" class="header__logo">
          <img :src="isActive ? logoActive : logoNormal" alt="亚科Logo">
        </router-link>
      </div>

      <!-- 右侧工具栏 -->
      <div class="header__right">
        <div class="header-tools" v-show="!isSearchActive">
          <!-- 搜索按钮 -->
          <div class="tool-btn search-btn" @click="showSearch">
            <i class="el-icon-search"></i>
          </div>

          <div class="world-icon">
            <img :src="isActive? dqActive : dqNormal" alt="world">
          </div>
          
          <!-- 语言切换 -->
          <LangSwitch :is-scrolled="isScrolled" />
          
          <!-- 菜单按钮 -->
          <div class="tool-btn menu-btn" @click="toggleMenu">
            <div class="hamburger" :class="{ 'is-active': isMenuOpen }">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- 搜索框 -->
      <div class="header-search" :class="{ 'is-active': isSearchActive }">
        <div class="search-wrapper">
          <i class="el-icon-search search-icon"></i>
          <input
            type="text"
            v-model="searchKeyword"
            :placeholder="$t('header.searchPlaceholder')"
            @keyup.enter="handleSearch"
            ref="searchInput"
          >
          <div class="close-btn" @click="closeSearch">
            <i class="el-icon-close"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- 移动端菜单 -->
    <div class="mobile-menu" :class="{ 'is-open': isMenuOpen }" v-if="isMenuOpen">
      <div class="menu-overlay" @click="closeMenu"></div>
      <div class="menu-content">
        <div class="menu-header">
          <div class="close-menu-btn" @click="closeMenu">
            <i class="el-icon-close"></i>
          </div>
        </div>
        
        <nav class="mobile-nav">
          <div
            v-for="(item, index) in menuItems"
            :key="item.path"
            class="mobile-nav-item"
            @click="handleMenuClick(item)"
          >
            <div class="nav-item-main">
              <span>{{  item.title }}</span>
              <i v-if="item.children && item.children.length"
                 class="el-icon-arrow-right submenu-arrow"
                 @click.stop="toggleSubmenu(index)">
              </i>
            </div>
            
            <!-- 子菜单 -->
            <div class="mobile-submenu" v-if="item.children && item.children.length"
                 :class="{ 'is-open': openSubmenus.includes(index) }">
              <div
                v-for="(subItem, subIndex) in item.children"
                :key="subIndex"
                class="mobile-submenu-item"
                @click="handleMenuClick(subItem)"
              >
                {{ subItem.title }}
              </div>
            </div>
          </div>
        </nav>
      </div>
    </div>
  </header>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import {getRoutes} from "@/api/menu";
import { pushWithLanguage } from '@/utils/routeUtils'

export default {
  name: 'TheHeader',
  components: {
    LangSwitch: () => import('@/components/LangSwitch')
  },
  data() {
    return {
      isScrolled: true,
      productsMenuItems: [], //产品中心配置项
      menuItems: [],
      newsTypes: [],
      newsMenu: [],
      isActive: true,
      logoNormal: require('@/assets/logo.png'),
      logoActive: require('@/assets/logo-active.png'),
      dqNormal: require('@/assets/images/dq.png'),
      dqActive: require('@/assets/images/dq-active.png'),
      isSearchActive: false,
      searchKeyword: '',
      isMenuOpen: false,
      openSubmenus: []
    }
  },
  computed: {
    ...mapState({
      currentLanguage: state => state.app.language
    }),
  },
  // 添加路由监听器
  watch: {
    currentLanguage(newValue) {
      this.initMenuCache()
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    this.initMenuCache();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    ...mapActions(['setLanguage']),

    async initMenuCache() {
      try {
        const routers = await getRoutes();
        if (routers.success) {
          // 动态构建菜单项
          const menuData = routers.data;
          this.menuItems = menuData.map(item => {
            const menuItem = {
              ...item,
              ...{path: '/' + (item.url || item.path || '')}
            };

            // 如果有子菜单，构建二级菜单
            if (item.children && item.children.length > 0) {
              menuItem.children = item.children.map(child => ({
                ...child ,
                ...{path: '/' + (item.url || '') + '/' + (child.url || '')} // 拼接父级url和子级url
              }));
            }
            return menuItem;
          });
          // 将路由数据存储到本地缓存
          localStorage.setItem('menuItems', JSON.stringify(this.menuItems));
        }
      } catch (e) {
        console.error('获取菜单数据失败', e);
        // 兜底：如果接口失败，尝试读取本地缓存
        let menuItems = localStorage.getItem('menuItems');
        if (menuItems) {
          try {
            this.menuItems = JSON.parse(menuItems);
          } catch (parseError) {
            console.error('解析菜单缓存失败', parseError);
          }
        }
      }
    },


    //菜单跳转
    handleMenuClick(item) {
      this.closeMenu()
      const currentPath = this.$route.path
      const currentQuery = this.$route.query.product
      const isSamePath = currentPath === item.path
      const isSameQuery = JSON.stringify(currentQuery) === JSON.stringify(item)
      if (isSamePath && isSameQuery) {
        // 已经在当前页面且参数一致，不做跳转
        return
      }
      // 使用 pushWithLanguage 进行跳转
      pushWithLanguage(item.path, this.currentLanguage).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('路由跳转失败:', err)
        }
      })
    },

    //获取产品中心的菜单配置项
    async getProductMenu() {
      try {
        const cache = localStorage.getItem('productsMenuItems')
        if (cache) {
          try {
            this.productsMenuItems = JSON.parse(cache)
            this.menuItems[0].subItems = this.productsMenuItems
          } catch (e) {
            console.error('解析产品中心菜单缓存失败', e)
          }
        }
      } catch (error) {
        console.error('获取产品分类失败:', error)
        this.$message.error('获取产品分类失败，请稍后重试')
      }
    },

    handleScroll() {
      // this.isScrolled = window.scrollY > 50
      // this.isActive = window.scrollY > 0
      
      // 当页面滚动时，自动关闭搜索框和菜单
      if (this.isSearchActive) {
        this.closeSearch()
      }
      if (this.isMenuOpen) {
        this.closeMenu()
      }
    },

    showSearch() {
      this.isSearchActive = true
      this.$nextTick(() => {
        this.$refs.searchInput.focus()
      })
    },

    closeSearch() {
      this.isSearchActive = false
      this.searchKeyword = ''
    },

    handleSearch() {
      if (this.searchKeyword.trim()) {
        pushWithLanguage('/SearchPage', this.currentLanguage).then(() => {
          // 跳转成功后更新查询参数
          this.$router.replace({
            path: this.$route.path,
            query: { keyword: this.searchKeyword }
          })
          this.closeSearch()
        }).catch(err => {
          console.error('搜索页面跳转失败:', err)
        })
      }
    },

    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
      if (this.isMenuOpen) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },

    closeMenu() {
      this.isMenuOpen = false
      this.openSubmenus = []
      document.body.style.overflow = ''
    },

    toggleSubmenu(index) {
      const submenuIndex = this.openSubmenus.indexOf(index)
      if (submenuIndex > -1) {
        this.openSubmenus.splice(submenuIndex, 1)
      } else {
        this.openSubmenus.push(index)
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '../assets/styles/variables.less';
@import '../assets/styles/mixins.less';

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1000;
  background: transparent;
  height: 56px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  /* 小米浏览器兼容性修复 */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  will-change: transform;
  /* 确保在小米浏览器中可见 */
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;

  .container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    padding: 0 14px;
    max-width: 100vw;
    width: 100%;
    /* 小米浏览器兼容性 */
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
  }

  &__left {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
  }

  &__logo {
    display: flex;
    align-items: center;
    img {
      height: 32px;
      width: auto;
      display: block;
    }
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .header-tools {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .world-icon {
    height: 20px;
    display: flex;
    align-items: center;
    position: relative;
    margin-left: 8px;
    img{
      width: 20px;
      height: 20px;
    }
  }

  .tool-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    cursor: pointer;
    
    i {
      color: #fff;
      font-size: 22px;
      transition: color 0.3s ease;
    }
    
    &:hover {
      background: rgba(255,255,255,0.1);
      i {
        color: #e0e0e0;
      }
    }
  }

  // 汉堡菜单样式
  .hamburger {
    width: 22px;
    height: 18px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    
    span {
      display: block;
      height: 3px;
      border-radius: 2px;
      background: #fff;
      transition: all 0.3s ease;
    }
    
    &.is-active span:nth-child(1) {
      transform: translateY(7.5px) rotate(45deg);
    }
    &.is-active span:nth-child(2) {
      opacity: 0;
    }
    &.is-active span:nth-child(3) {
      transform: translateY(-7.5px) rotate(-45deg);
    }
  }

  // 语言切换样式
  .lang-switch {
    min-width: 0;
    :deep(.lang-label) {
      color: #fff !important;
      font-size: 15px !important;
      font-weight: 400;
      letter-spacing: 0.5px;
      transition: color 0.3s ease;
    }
    :deep(.lang-label):hover {
      color: #e0e0e0 !important;
    }
    :deep(.el-dropdown) {
      min-width: 0;
    }
  }

  // 滚动后的样式
  &--scrolled {
    background: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

    .tool-btn {
      i {
        color: #333;
      }
      
      &:hover {
        background: rgba(0, 0, 0, 0.05);
        i {
          color: #666;
        }
      }
    }

    // 汉堡菜单滚动后颜色
    .hamburger span {
      background: #333;
    }

    // 语言切换滚动后颜色
    .lang-switch {
      :deep(.lang-label) {
        color: #333 !important;
      }
      :deep(.lang-label):hover {
        color: #666 !important;
      }
    }
  }
}

.header-search {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 56px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1100;
  /* 小米浏览器兼容性 */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);

  &.is-active {
    opacity: 1;
    visibility: visible;
  }

  .search-wrapper {
    width: 90vw;
    max-width: 400px;
    height: 38px;
    display: flex;
    align-items: center;
    background: #f5f5f5;
    border-radius: 19px;
    position: relative;
    padding: 0 36px 0 30px;
  }

  .search-icon {
    position: absolute;
    left: 8px;
    font-size: 16px;
    color: #999;
  }

  input {
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    font-size: 15px;
    color: #333;
    outline: none;
    padding: 0;
    &::placeholder {
      color: #bbb;
    }
  }

  .close-btn {
    position: absolute;
    right: 6px;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 50%;
    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
    i {
      font-size: 18px;
      color: #999;
    }
  }
}

/* 移动端菜单样式 */
.mobile-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1200;
  display: flex;
  /* 小米浏览器兼容性 */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;

  .menu-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.3);
    z-index: 1;
  }

  .menu-content {
    position: relative;
    z-index: 2;
    width: 80vw;
    max-width: 320px;
    height: 100vh;
    background: #fff;
    box-shadow: 2px 0 12px rgba(0,0,0,0.08);
    display: flex;
    flex-direction: column;
    animation: slideInMenu 0.3s;
  }

  .menu-header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 16px 18px;
    border-bottom: 1px solid #f0f0f0;
    h3 {
      font-size: 17px;
      color: #333;
      margin: 0;
    }
    .close-menu-btn {
      cursor: pointer;
      i {
        font-size: 22px;
        color: #999;
      }
    }
  }

  .mobile-nav {
    flex: 1;
    overflow-y: auto;
    padding: 10px 0;
    .mobile-nav-item {
      padding: 0 18px;
      border-bottom: 1px solid #f5f5f5;
      .nav-item-main {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 44px;
        font-size: 15px;
        color: #333;
        cursor: pointer;
      }
      .submenu-arrow {
        font-size: 15px;
        color: #bbb;
        margin-left: 8px;
      }
      .mobile-submenu {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.3s;
        background: #fafbfc;
        &.is-open {
          max-height: 500px;
        }
        .mobile-submenu-item {
          padding: 10px 0 10px 20px;
          font-size: 14px;
          color: #666;
          cursor: pointer;
          &:hover {
            color: #19529F;
            background: #f0f6ff;
          }
        }
      }
    }
  }
}

@keyframes slideInMenu {
  from { transform: translateX(-100%);}
  to { transform: translateX(0);}
}
</style>